import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { WidgetProvider } from "../context/WidgetContext";
import { WidgetLayer } from "./WidgetLayer";
import { useAuth } from "../hooks/useAuth";
import { LiveDot, Mono } from "./v3";

/**
 * App shell — responsive: desktop has a fixed 220px sidebar + 60px topbar;
 * mobile collapses the sidebar into a slide-in drawer toggled from the topbar.
 *
 * The atmospheric `.stage` backdrop is mounted once at the shell root so it
 * persists across route changes without re-animating.
 */

interface NavItem {
  to: string;
  label: string;
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/app/home", label: "Home" },
  { to: "/app/calls", label: "Calls" },
  { to: "/app/jobs", label: "Jobs & messages" },
  { to: "/app/chat", label: "Chat" },
  { to: "/app/memory", label: "Memory" },
  { to: "/app/policy", label: "Policy" },
  { to: "/app/settings", label: "Settings" },
];

export function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <WidgetProvider>
      <div className="relative h-screen overflow-hidden">
        <div className="stage" aria-hidden="true" />

        <div className="relative z-10 flex h-full">
          {/* Desktop sidebar — hidden below md */}
          <div className="hidden md:flex">
            <Sidebar />
          </div>

          {/* Mobile drawer */}
          {drawerOpen && (
            <>
              <div
                onClick={() => setDrawerOpen(false)}
                className="md:hidden fixed inset-0 bg-bg/80 backdrop-blur-sm z-40"
                aria-hidden="true"
              />
              <div className="md:hidden fixed inset-y-0 left-0 w-[260px] z-50 shadow-2xl">
                <Sidebar onNavClick={() => setDrawerOpen(false)} />
              </div>
            </>
          )}

          <div className="flex-1 flex flex-col min-w-0">
            <TopBar onMenuClick={() => setDrawerOpen(true)} />
            <main className="flex-1 overflow-auto">
              <Outlet />
            </main>
          </div>
        </div>

        <WidgetLayer />
      </div>
    </WidgetProvider>
  );
}

function Sidebar({ onNavClick }: { onNavClick?: () => void }) {
  return (
    <aside className="w-[220px] shrink-0 h-full border-r border-line bg-bg/80 backdrop-blur-md flex flex-col">
      <div className="px-5 pt-5 pb-7">
        <NavLink
          to="/app/home"
          onClick={onNavClick}
          className="inline-flex items-center gap-2.5"
        >
          <BrandMark />
          <span className="font-display text-[19px] tracking-tight font-medium">
            Vooice
          </span>
        </NavLink>
      </div>

      <nav className="flex-1 px-3 flex flex-col gap-0.5">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavClick}
            className={({ isActive }) =>
              [
                "group relative flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors",
                isActive
                  ? "text-t1 bg-white/[0.04]"
                  : "text-t2 hover:text-t1 hover:bg-white/[0.02]",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={[
                    "absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[2px] rounded-r-sm transition-opacity",
                    isActive ? "bg-amber opacity-100" : "opacity-0",
                  ].join(" ")}
                  aria-hidden="true"
                />
                <span className="font-medium tracking-tight">{item.label}</span>
                {item.badge != null && (
                  <span className="font-mono text-[10px] tracking-wider text-t3">
                    {item.badge}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <SidebarFooter />
    </aside>
  );
}

function SidebarFooter() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  async function handleLogout() {
    setSigningOut(true);
    try {
      await logout();
      navigate("/login", { replace: true });
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className="px-3 pb-4 pt-3 border-t border-line-soft">
      <div className="px-3 pb-2">
        <Mono className="block mb-2">This month</Mono>
        <div className="flex items-baseline justify-between">
          <span className="font-display text-lg leading-none">83</span>
          <Mono>min</Mono>
        </div>
        <div className="mt-2 h-[3px] rounded-full bg-line-soft overflow-hidden">
          <div className="h-full bg-amber" style={{ width: "41%" }} />
        </div>
      </div>
      <button
        onClick={handleLogout}
        disabled={signingOut}
        className="w-full mt-2 px-3 py-2 rounded-md text-left text-sm text-t3 hover:text-t1 hover:bg-white/[0.02] disabled:opacity-50 transition-colors"
      >
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
    </div>
  );
}

function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  return (
    <header className="h-[60px] shrink-0 px-4 md:px-6 border-b border-line flex items-center justify-between bg-bg/30 backdrop-blur-md">
      <div className="flex items-center gap-3">
        {/* Mobile-only hamburger */}
        <button
          onClick={onMenuClick}
          className="md:hidden -ml-1 size-9 inline-flex items-center justify-center rounded-md text-t2 hover:text-t1 hover:bg-white/5 transition-colors"
          aria-label="Open menu"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        <LiveDot tone="amber" />
        <Mono size="sm" tone="t2" className="hidden sm:inline-flex">
          All systems nominal
        </Mono>
      </div>

      <div className="flex items-center gap-3 md:gap-5">
        <Mono size="sm" tone="t2" className="hidden md:inline-flex">
          +1 (516) 272&#8209;7027
        </Mono>
        <Avatar name="Operator" />
      </div>
    </header>
  );
}

function BrandMark() {
  return (
    <svg
      width="22"
      height="16"
      viewBox="0 0 56 40"
      fill="none"
      className="text-amber"
      aria-hidden="true"
    >
      <path
        d="M2 14 L10 26 L18 18 L26 30 L34 18 L42 26 L54 14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <div className="size-8 rounded-full bg-bg-2 border border-line flex items-center justify-center">
      <span className="font-mono text-[10px] tracking-[0.16em] text-t2">
        {initials}
      </span>
    </div>
  );
}
